import {
  AccountBrand,
  AccountConfig,
  CustomCode,
  DynamicPage,
  SectionData,
  Website,
  WebsiteData,
  WithId,
} from '@betterfunnel/types';

const cleanObj = (obj?: any) => {
  if (!obj) return obj;
  if (typeof obj !== 'object') return obj;

  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, v]) => !!v)
      .map(([k, v]) => [k, v]),
  );
};

/**
 * Merges custom codes from account, campaign, and website levels.
 * - Account level defines base custom codes with their default enabled state
 * - Campaign level can add new codes and override enabled state via references
 * - Website level can add new codes and override enabled state via references
 *
 * @param accountConfig - Account level configuration
 * @param campaignConfig - Campaign level configuration
 * @param websiteConfig - Website level configuration
 * @returns Array of merged custom codes with their final enabled state
 */
export const mergeCustomCodes = (
  accountConfig?: AccountConfig,
  campaignConfig?: AccountConfig,
  websiteConfig?: AccountConfig,
): CustomCode[] => {
  // Start with account-level custom codes
  const customCodesMap = new Map<string, CustomCode>();

  // Add account level custom codes
  if (accountConfig?.customCodes) {
    accountConfig.customCodes.forEach((code) => {
      customCodesMap.set(code.id, { ...code });
    });
  }

  // Add campaign level custom codes (new ones only)
  if (campaignConfig?.customCodes) {
    campaignConfig.customCodes.forEach((code) => {
      if (!customCodesMap.has(code.id)) {
        customCodesMap.set(code.id, { ...code });
      }
    });
  }

  // Add website level custom codes (new ones only)
  if (websiteConfig?.customCodes) {
    websiteConfig.customCodes.forEach((code) => {
      if (!customCodesMap.has(code.id)) {
        customCodesMap.set(code.id, { ...code });
      }
    });
  }

  // Apply campaign level references (enabled/disabled overrides)
  if (campaignConfig?.customCodesReferences) {
    campaignConfig.customCodesReferences.forEach((ref) => {
      const code = customCodesMap.get(ref.id);
      if (code) {
        code.enabled = ref.enabled;
      }
    });
  }

  // Apply website level references (enabled/disabled overrides)
  if (websiteConfig?.customCodesReferences) {
    websiteConfig.customCodesReferences.forEach((ref) => {
      const code = customCodesMap.get(ref.id);
      if (code) {
        code.enabled = ref.enabled;
      }
    });
  }

  return Array.from(customCodesMap.values());
};

export const applyExtras = (
  websiteData: WebsiteData,
  campaign: DynamicPage | undefined,
  brand: AccountBrand | undefined,
  config: AccountConfig | undefined,
  globalSections: WithId<SectionData>[] | undefined,
): WebsiteData => {
  const applySection = (section: SectionData): SectionData => {
    if (section.global?.globalId && globalSections) {
      const globalSection = globalSections.find(
        (s) => s.id === section.global?.globalId,
      );
      if (globalSection) {
        return {
          ...section,
          ...globalSection,
          id: section.id,
          global: section.global,
        } as SectionData;
      }
    }
    if (section.type === 'footer' && config) {
      const newLinks = section.links;
      if (config.termsOfUseLink) {
        const termsLink = newLinks.find((l) => l.type === 'terms');
        if (termsLink) termsLink.href = config.termsOfUseLink;
      }
      if (config.privacyPolicyLink) {
        const privacyLink = newLinks.find((l) => l.type === 'privacy');
        if (privacyLink) privacyLink.href = config.privacyPolicyLink;
      }
      if (config.links) {
        newLinks.forEach((l) => {
          if (l.type === 'terms' || l.type === 'privacy') return;
          const href = config.links?.[l.type];
          if (href) l.href = href;
        });
      }
    }

    return section;
  };

  // Merge custom codes from all levels
  let mergedCustomCodes: CustomCode[] = [];
  try {
    mergedCustomCodes = mergeCustomCodes(
      config,
      campaign?.config,
      websiteData.config,
    );
  } catch (error) {
    console.error('Error merging custom codes:', error);
    // Fallback to empty array on error
    mergedCustomCodes = [];
  }

  return {
    ...websiteData,
    sections: websiteData.sections.map(applySection),
    brand: {
      ...brand,
      ...campaign?.brand,
      ...websiteData?.brand,
    },
    config: {
      ...cleanObj(config),
      ...cleanObj(campaign?.config),
      ...cleanObj(websiteData.config),
      customCodes: mergedCustomCodes,
      seo: {
        ...cleanObj(config?.seo),
        ...cleanObj(campaign?.config?.seo),
        ...cleanObj(websiteData.config?.seo),
      },
    },
    language: websiteData.language || campaign?.language,
  };
};

export const applyExtraData = (
  websiteData: Website,
  campaign: DynamicPage | undefined,
  brand: AccountBrand | undefined,
  config: AccountConfig | undefined,
  globalSections: WithId<SectionData>[] | undefined,
): Website => {
  return {
    ...websiteData,
    development: applyExtras(
      websiteData.development,
      campaign,
      brand,
      config,
      globalSections,
    ),
    production: websiteData.production
      ? applyExtras(
          websiteData.production,
          campaign,
          brand,
          config,
          globalSections,
        )
      : undefined,
  };
};

export const getGlobalSectionsUsed = (
  website: Website,
): NonNullable<SectionData['global']>[] => {
  const sections = website.production?.sections || [];
  return sections
    .filter((s) => s.global)
    .map((s) => s.global!)
    .filter((s) => !!s);
};
